import {
  useMemo,
  useState,
  useRef,
  useEffect,
  type ComponentProps,
  forwardRef,
} from 'react'

import { Skeleton } from '~/ui'
import { cn } from '~/utils/classnames'
import {
  getImageAttributes,
  convertTransformationToString,
  parseSizes,
  buildSrcSet,
  type TransformationOptions,
  type Sizes,
  defaultImageFormat,
} from '~/utils/image'

export type ImageProps = {
  src: string
  alt?: string
  width?: number
  height?: number
  transformation?: TransformationOptions
  className?: string
  draggable?: boolean
  loading?: 'lazy' | 'eager'
  sizes?: Sizes
} & Omit<ComponentProps<'img'>, 'src' | 'alt' | 'sizes'>

export const Image = forwardRef<HTMLImageElement, ImageProps>(
  (
    {
      src,
      alt = '',
      width,
      height,
      transformation,
      className,
      draggable = false,
      onLoad,
      onError,
      loading = 'lazy',
      sizes,
      ...props
    },
    ref
  ) => {
    const [isLoading, setIsLoading] = useState(true)

    const transformationString = useMemo(() => {
      if (transformation) {
        const transformationWithFormat = {
          ...transformation,
          format: transformation.format || defaultImageFormat,
        }
        return convertTransformationToString(transformationWithFormat)
      }
      return convertTransformationToString({ format: defaultImageFormat })
    }, [transformation])

    const imageAttributes = getImageAttributes(src, {
      width,
      height,
      transformation: transformationString,
      alt,
    })

    const imgRef = useRef<HTMLImageElement>(null)

    // TODO: the image onLoad is not working as expected, this was a quick fix to fix it
    useEffect(() => {
      const img = imgRef.current

      if (img) {
        const handleLoad = () => {
          setIsLoading(false)
        }

        const handleError = () => {
          setIsLoading(false)
        }

        img.addEventListener('load', handleLoad)
        img.addEventListener('error', handleError)

        // Check if image is already loaded (cached)
        if (img.complete) {
          setIsLoading(false)
        }

        return () => {
          img.removeEventListener('load', handleLoad)
          img.removeEventListener('error', handleError)
        }
      }
    }, [])

    const sizesAttr =
      typeof sizes === 'string' ? sizes : sizes ? parseSizes(sizes) : undefined

    const srcSetAttr = useMemo(() => {
      return buildSrcSet(src, sizes, {
        height,
        transformation: transformationString,
      })
    }, [sizes, transformationString, height, src])

    return (
      <div className="relative h-full w-full">
        {isLoading && (
          <Skeleton className="rounded-inherit absolute inset-0 h-full w-full" />
        )}
        <img
          {...imageAttributes}
          ref={ref || imgRef}
          alt={imageAttributes.alt}
          width={width}
          height={height}
          draggable={draggable}
          loading={loading}
          sizes={sizesAttr}
          srcSet={srcSetAttr}
          className={cn(
            'relative h-full w-full object-cover',
            'transition-opacity duration-200',
            isLoading ? 'opacity-0' : 'opacity-100',
            className
          )}
          {...props}
        />
      </div>
    )
  }
)

Image.displayName = 'Image'
